import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/layout/Layout';
import {
  Shield,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Car,
  User,
  Download,
  Clock,
  Phone,
  Mail
} from 'lucide-react';
import { formatDate } from '../utils/helpers';
import { mockApi } from '../services/mockApi';

const ClienteGarantiaPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [garantia, setGarantia] = useState(null);

  const cargarGarantia = useCallback(async () => {
    try {
      setLoading(true);
      const clienteRes = await mockApi.getClienteByEmail(user.email);
      const data = await mockApi.getGarantiaCliente(clienteRes.data.id);
      setGarantia(data);
    } catch (error) {
      console.error('Error al cargar garantía:', error);
    } finally {
      setLoading(false);
    }
  }, [user.email]);

  useEffect(() => {
    if (user) {
      cargarGarantia();
    }
  }, [user, cargarGarantia]);

  const descargarCertificado = async () => {
    try {
      const clienteRes = await mockApi.getClienteByEmail(user.email);
      await mockApi.descargarCertificadoPDF(clienteRes.data.id, garantia.garantia.id);
      alert('Certificado descargado correctamente');
    } catch (error) {
      console.error('Error al descargar certificado:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-700 mx-auto" style={{ animation: 'spin 1s linear infinite' }}></div>
            <p className="mt-4 text-gray-600">Cargando información de garantía...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!garantia) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-warning-500 mx-auto mb-4" />
            <p className="text-gray-600">No se encontró información de garantía</p>
          </div>
        </div>
      </Layout>
    );
  }

  const { garantia: garantiaData, vehiculo, cobertura } = garantia.data;
  const diasRestantes = garantiaData.diasRestantes || 0;
  const estaProximoVencer = garantiaData.proximaVencer || false;

  return (
    <Layout>
      <div className="bg-gray-50 min-h-screen p-4 md:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Mi Garantía
              </h1>
              <p className="text-gray-500 mt-1">Información detallada de tu garantía extendida</p>
            </div>
          </div>

          {estaProximoVencer && (
            <div className="bg-warning-50 border-l-4 border-warning-500 p-4 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-warning-600 mr-3" />
                <div>
                  <p className="text-warning-900 font-semibold">Tu garantía está próxima a vencer</p>
                  <p className="text-warning-700 text-sm">Quedan {diasRestantes} días. Contacta con nosotros para renovarla.</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-lg shadow-soft p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-3 bg-primary-700 rounded-lg">
                      <Shield className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Información de Garantía</h2>
                      <p className="text-sm text-gray-500">N.º {garantiaData.numeroGarantia}</p>
                    </div>
                  </div>
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                    garantiaData.estado === 'activa'
                      ? 'bg-success-50 text-success-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {garantiaData.estado === 'activa' ? 'Activa' : garantiaData.estado}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                    <Calendar className="h-5 w-5 text-primary-700 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Fecha de inicio</p>
                      <p className="font-semibold text-gray-900">{formatDate(garantiaData.fechaAlta)}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                    <Clock className="h-5 w-5 text-gray-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Fecha de fin</p>
                      <p className="font-semibold text-gray-900">{formatDate(garantiaData.fechaVencimiento)}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Días restantes</p>
                      <p className="text-2xl font-bold text-gray-900">{diasRestantes > 0 ? diasRestantes : 0}</p>
                    </div>
                    <button
                      onClick={descargarCertificado}
                      className="flex items-center space-x-2 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800"
                    >
                      <Download className="h-4 w-4" />
                      <span>Descargar Certificado</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-soft p-6 border border-gray-200">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-3 bg-success-600 rounded-lg">
                    <Car className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Vehículo Asegurado</h2>
                    <p className="text-sm text-gray-500">Información del vehículo cubierto</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Marca y Modelo</p>
                    <p className="font-semibold text-gray-900">{vehiculo.marca} {vehiculo.modelo}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Matrícula</p>
                    <p className="font-semibold text-gray-900">{vehiculo.matricula}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Año</p>
                    <p className="font-semibold text-gray-900">{vehiculo.year}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Kilometraje</p>
                    <p className="font-semibold text-gray-900">{vehiculo.kilometros?.toLocaleString() || 0} km</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-soft p-6 border border-gray-200">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-3 bg-primary-700 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Cobertura</h2>
                    <p className="text-sm text-gray-500">Componentes y servicios incluidos</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Componentes Cubiertos</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {cobertura.componentesCubiertos.map((componente, index) => (
                        <div key={index} className="flex items-center space-x-2 p-3 bg-success-50 rounded-lg">
                          <CheckCircle className="h-4 w-4 text-success-600 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{componente}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Servicios Incluidos</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {cobertura.serviciosIncluidos.map((servicio, index) => (
                        <div key={index} className="flex items-center space-x-2 p-3 bg-primary-50 rounded-lg">
                          <CheckCircle className="h-4 w-4 text-primary-700 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{servicio}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-soft p-6 border border-gray-200">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-3 bg-gray-700 rounded-lg">
                    <User className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Titular</h2>
                    <p className="text-sm text-gray-500">Datos del propietario</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Nombre completo</p>
                    <p className="font-semibold text-gray-900">{user.nombre}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-semibold text-gray-900">{user.email}</p>
                  </div>
                </div>
              </div>

              <div className="bg-primary-700 rounded-lg shadow-soft p-6 text-white">
                <h3 className="font-bold text-lg mb-4">Necesitas ayuda?</h3>
                <p className="text-sm text-primary-100 mb-4">
                  Nuestro equipo está disponible para resolver cualquier duda sobre tu garantía.
                </p>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <span>900 123 456</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <span>soporte@rdgarantia.com</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>Lun-Vie: 9:00 - 18:00</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ClienteGarantiaPage;
